
import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseUrl } from "../../../comman/config";
import { useLocation, Link, useNavigate, useParams } from 'react-router-dom';
import ReceipeForm from "../../../components/Form/Master/ReceipeForm";
import { isViewOnly } from "../../../comman/permissions";
import * as XLSX from 'xlsx';

// Edit Recipe Master, and -- on /receipe/view/:id -- View, which is the same screen with nothing
// on it that writes. One component rather than two because the two differ only in that: the
// subject is found the same way, the exports are the same exports, and a copy would be a second
// place to fix the next time either changes.
//
// View is also what an Edit URL gives somebody whose operations on Recipe Master stop at View --
// the client's rule holds whichever way the screen is reached, not only through the View button.
// The router turns most of them away before this renders (an /edit/ route asks for Update), and
// the endpoints refuse the save regardless; this is what they see.
const RECIPE_SCREEN = '/receipe/list';

// The two computed columns of the Recipe Master table, for the export -- the same arithmetic
// ReceipeForm renders them with, so the sheet reads what the screen does:
//   Std. RAS QTY (100 persons) = STD RAS * (Qty per 100 kgs / 100)
//   % weightage                = Std. RAS QTY / STD RAS, main items only
// Both blank while the recipe has no STD RAS. stdRas is that maharaj's recipe's, which
// receipe_master carries on every row.
export const recipeExportStdColumns = (ingredient, stdRasValue) => {
    const stdRas = parseFloat(stdRasValue);
    const q = parseFloat(ingredient?.qty);
    if (isNaN(stdRas) || String(stdRasValue ?? '').trim() === '' || isNaN(q)) return ['', ''];
    const stdRasQty = (stdRas * (q / 100)).toFixed(3);
    const weightage = ingredient?.is_main && stdRas
        ? `${((parseFloat(stdRasQty) / stdRas) * 100).toFixed(2)}%`
        : '';
    return [stdRasQty, weightage];
};

export default function EditMenuitem() {

    const [ingredientList, setIngredientList] = useState([]);
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    // This screen used to read its subject purely from router state, which meant it only worked
    // when navigated to from within the app. Recipe Planning's "Edit Process" now opens it in a
    // new tab, and a new tab carries no state -- so when there is none, the same detail is rebuilt
    // from the URL: the id is already in the path, and the maharaj comes from ?maharaj=.
    const [derivedDetail, setDerivedDetail] = useState(null);
    const detail = location.state ?? derivedDetail;

    const readOnly = location.pathname.startsWith('/receipe/view') || isViewOnly(RECIPE_SCREEN);

    useEffect(() => {
        if (location.state) return;
        const load = async () => {
            try {
                const itemId = Number(atob(params.id));
                const maharajId = new URLSearchParams(location.search).get('maharaj');
                const [itemRes, maharajRes] = await Promise.all([
                    axios.get(`${baseUrl}/comman/list?table=item_master&condition=id=${itemId}`),
                    maharajId
                        ? axios.get(`${baseUrl}/comman/list?table=maharaj_master&condition=id=${Number(maharajId)}`)
                        : Promise.resolve({ data: { data: [] } }),
                ]);
                const item = itemRes.data.data?.[0];
                if (!item) return;
                setDerivedDetail([{ ...item, maharaj: maharajRes.data.data?.[0] ?? null }]);
            } catch (error) {
                console.error('Could not load the recipe from the URL', error);
            }
        };
        load();
    }, [location.state, location.search, params.id]);

    const handleExport = async() => {
        const response = await axios.post(`${baseUrl}/receipe/export`, {menu_item_id : detail[0].id});

        const data = response.data.data;

        const itemName = detail[0]?.item_name;
        const engName = detail[0]?.eng_name;
        const itemDisplayName = itemName || engName ? `${itemName ?? ''}${engName ? ` (${engName})` : ''}`.trim() : '-';
        // Separate the data by maharaj_name
        const separatedData = data.reduce((acc, curr) => {
            const { maharaj_name } = curr;
        
            if (!acc[maharaj_name]) {
                acc[maharaj_name] = [];
            }
            acc[maharaj_name].push(curr);
            return acc;
        }, {});

        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // Header row for the ingredients data
        const header = ["Id", "Ingredients", "Qty/100kg", "Unit", "Std RAS Qty (100 persons)", "% Weightage"];

        // Item name array
        const itemNameArray = ['Item Name:', itemDisplayName];

        // Add separate sheets for each maharaj_name
        for (const [maharaj, ingredients] of Object.entries(separatedData)) {
            const stdRas = ingredients.find((i) => i.std_ras !== null && i.std_ras !== undefined)?.std_ras;
            // Convert the ingredients to a 2D array format (rows and columns) for the sheet
            const maharajArray = ingredients.map((ingredient, index) => [
                index+1,
                `${ingredient?.ingredient || ingredient?.eng_name ? `${ingredient?.ingredient ?? ''}${ingredient?.eng_name ? ` (${ingredient.eng_name})` : ''}`.trim() : '-'}`,
                ingredient.qty,
                ingredient.unit,
                ...recipeExportStdColumns(ingredient, stdRas),
            ]);
        
            // Create a final array combining item name row, maharaj data, and header
            const finalData = [
                itemNameArray,    // Item name row at the top
                [],               // Empty row for spacing
                header,
                ...maharajArray,
            ];
        
            // Convert finalData array to sheet format
            const ws = XLSX.utils.aoa_to_sheet(finalData);
            XLSX.utils.book_append_sheet(wb, ws, maharaj);
        }

        // Write the Excel file
        const fileName = `${itemName}_${engName}_Recipe.xlsx`.replace(/\s+/g, '_');
        XLSX.writeFile(wb, fileName);
       
    }

    const handleExportProcess = async() => {
        const condition = `menu_item_id = ${detail[0].id} AND maharaj = ${detail[0].maharaj?.id}`;
        const response = await axios.get(`${baseUrl}/comman/list?table=receipe_master_process&condition=${condition}`);

        const processList = response.data.data;
        const itemName = detail[0]?.item_name;
        const engName = detail[0]?.eng_name;
        const maharajName = detail[0].maharaj?.maharaj_name ?? "";
        const itemDisplayName = itemName || engName ? `${itemName ?? ''}${engName ? ` (${engName})` : ''}`.trim() : '-';

        const header = ["Step", "Process"];
        const itemNameArray = ["Item Name", itemDisplayName];
        const maharajArray = ["Maharaj Name", maharajName];
        console.log(processList);
        // Prepare data for export
        const data = [
            itemNameArray, // Item name row
            maharajArray,
            [], // Empty row for spacing
            header, // Header row
            ...processList.map((item, index) => {
                return [
                    index + 1,
                    item?.process ?? ""
                ];
            })
        ];

        // Create a worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        
        // Create a new workbook and append the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Recipe Process');

        // Generate a file and trigger download
        const fileName = `${itemName}_${engName}_Process.xlsx`.replace(/\s+/g, '_');
        XLSX.writeFile(workbook, fileName);
    }
    const handelIngredientList = (list) => {
        setIngredientList(list);
    }

    const handleDuplicate = () => {
        navigate(`/receipe/add`, { state: detail });
    }

    // Back returns to the list narrowed to the item just edited, rather than to all 685 recipes
    // with the one you were looking at somewhere among them. The list reads ?item= and sets its
    // own filter from it -- the same query string Recipe Planning's "Recipe Master" button uses.
    // Falls back to the plain list before the subject has loaded.
    const backToList = detail?.[0]?.id ? `/receipe/list?item=${detail[0].id}` : "/receipe/list";

  return (
    <div className="p-4">
        <div className="flex justify-between items-center py-4 px-6">
            <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">{readOnly ? 'View Recipe Master' : 'Edit Recipe Master'}</h1>
            <div>
            {/* Duplicate lands on Add and Add Ingredient creates one: both are writes, and
                neither is offered to a reader. The two exports are not -- they read the recipe
                that is already on screen. */}
            {!readOnly && <button onClick={handleDuplicate} disabled={!detail} className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-2 disabled:opacity-50">Duplicate</button>}
                <button onClick={handleExport} disabled={!detail} className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-2 disabled:opacity-50">Export Recipe</button>
                <button onClick={handleExportProcess} disabled={!detail} className="px-4 py-2 bg-blue-500 text-white rounded-md placeholder:text-sm mr-2 disabled:opacity-50">Export Process</button>
                {!readOnly && <a href="/ingredients/add" target="_blank">
                    <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Ingredient</button>
                </a>}
                <Link to={backToList}>
                    <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                </Link>
            </div>
        </div>
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg px-4 py-2">
           <ReceipeForm detail={detail} handelIngredientList={handelIngredientList} readOnly={readOnly} />
        </div>
    </div>
  )
}
